import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { HeartbeatResponse } from '@lp/shared';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Clock,
  FileText,
  FolderOpen,
  ListChecks,
  MessageSquare,
  MousePointerClick,
  PanelRightClose,
  PlayCircle,
  Presentation,
  X,
} from 'lucide-react';
import { api, ApiError } from '../lib/api.js';
import { learnerKeys, useSessionDetail } from '../lib/useLearnerData.js';
import { useMediaQuery, WIDE_SCREEN_QUERY } from '../lib/useMediaQuery.js';
import { formatMinutes } from '../lib/format.js';
import { useToast } from '../components/Toast.js';
import { PageContainer } from '../components/layout/AppShell.js';
import { Alert, Badge, Button, Card, EmptyState, IconButton, Tabs, clsx } from '../components/ui.js';
import { VideoPlayer, type VideoPlayerHandle } from '../components/learner/VideoPlayer.js';
import { ChatPanel } from '../components/learner/ChatPanel.js';
import { NotesSection } from '../components/learner/NotesSection.js';
import { InteractiveSection } from '../components/learner/InteractiveSection.js';
import { QuizRunner } from '../components/learner/QuizRunner.js';
import { AssignmentSection } from '../components/learner/AssignmentSection.js';
import { ResourcesSection } from '../components/learner/ResourcesSection.js';
import { DecksSection } from '../components/learner/DeckViewer.js';
import { ApiErrorView, SessionSkeleton } from '../components/learner/StateViews.js';
import { NotFoundPage } from './NotFoundPage.js';

type SessionTab =
  | 'video'
  | 'deck'
  | 'notes'
  | 'interactive'
  | 'quiz'
  | 'assignment'
  | 'resources';

const TAB_META: Record<SessionTab, { label: string; icon: ReactNode }> = {
  video: { label: 'Video', icon: <PlayCircle size={15} aria-hidden /> },
  deck: { label: 'Deck', icon: <Presentation size={15} aria-hidden /> },
  notes: { label: 'Notes', icon: <FileText size={15} aria-hidden /> },
  interactive: { label: 'Interactive', icon: <MousePointerClick size={15} aria-hidden /> },
  quiz: { label: 'Quiz', icon: <ListChecks size={15} aria-hidden /> },
  assignment: { label: 'Assignment', icon: <ClipboardList size={15} aria-hidden /> },
  resources: { label: 'Resources', icon: <FolderOpen size={15} aria-hidden /> },
};

/** The tabs the assistant is offered beside — the lecture material it can talk about. */
const CHAT_TABS: ReadonlySet<SessionTab> = new Set(['video', 'deck', 'notes']);

/** Whether the learner collapsed the assistant on a wide screen, remembered in this browser. */
const CHAT_COLLAPSED_KEY = 'lp.session.chatCollapsed';

function readChatCollapsed(): boolean {
  try {
    return window.localStorage.getItem(CHAT_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeChatCollapsed(collapsed: boolean): void {
  try {
    if (collapsed) window.localStorage.setItem(CHAT_COLLAPSED_KEY, '1');
    else window.localStorage.removeItem(CHAT_COLLAPSED_KEY);
  } catch {
    // A preference, not a record: without storage it lasts until the page is left.
  }
}

/**
 * A session: the lecture, the assistant beside it, and everything the author
 * attached to it, one tab each in the order the spec fixes — video, deck,
 * notes, interactive, quiz, assignment — then the reference documents. Every
 * part is optional and gets a tab only when it exists; the page opens on the
 * first tab there is.
 * The decks are drawn only once their tab is first opened, so a learner who
 * never looks at them never downloads them; with several decks, each is drawn
 * only once its own sub-tab is opened.
 *
 * The assistant ("Ask AI") sits beside the video, deck and notes only. On the
 * other tabs its column is taken away and the content gets the full width.
 * On a wide screen the learner can also collapse it, which gives the lecture
 * the full width until a floating "Ask AI" button brings it back.
 *
 * Every panel stays mounted and is merely `hidden` when its tab is not
 * selected, so switching tabs does not lose a half-answered quiz, an unsent
 * assignment draft or the player's position. The video is paused on the way
 * out so it does not keep playing unseen.
 *
 * The chat panel is mounted exactly once and moved by layout rather than
 * re-created per breakpoint. That is not a styling nicety: the conversation
 * lives in that component's state and nowhere else, so remounting it would
 * silently throw the thread away when someone rotated their phone.
 */
export function SessionPage() {
  const { courseSlug = '', sessionSlug = '' } = useParams<{
    courseSlug: string;
    sessionSlug: string;
  }>();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const wide = useMediaQuery(WIDE_SCREEN_QUERY);

  const [chatOpen, setChatOpen] = useState(false);
  const [chatCollapsed, setChatCollapsedState] = useState(readChatCollapsed);
  const setChatCollapsed = (collapsed: boolean): void => {
    setChatCollapsedState(collapsed);
    writeChatCollapsed(collapsed);
  };
  // Remembered per session, so moving to the next one opens on its video again.
  const [chosenTab, setChosenTab] = useState<{ sessionId: string; tab: SessionTab } | null>(null);
  // The session whose deck tab has been opened; the deck is drawn from then on.
  const [deckOpenedFor, setDeckOpenedFor] = useState<string | null>(null);
  const playerRef = useRef<VideoPlayerHandle>(null);

  const queryKey = learnerKeys.session(courseSlug, sessionSlug);
  const { data, isPending, error, refetch } = useSessionDetail(courseSlug, sessionSlug);

  // With no video there is nothing to watch past the threshold, so the
  // learner says they are done themselves.
  const markDone = useMutation({
    mutationFn: (sessionId: string) =>
      api.post<HeartbeatResponse>(`/sessions/${encodeURIComponent(sessionId)}/complete`, {}),
    onError: () => {
      toast({ tone: 'error', title: 'Could not mark the session as done', body: 'Try again in a moment.' });
    },
  });

  // Esc closes the mobile drawer; on a wide screen there is nothing to close.
  useEffect(() => {
    if (!chatOpen || wide) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setChatOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [chatOpen, wide]);

  if (error instanceof ApiError && error.isNotFound) {
    return (
      <NotFoundPage
        title="That session is not here"
        description="No session at this address belongs to a course you can open. It may have been renamed or never published."
      />
    );
  }

  if (isPending) {
    return (
      <PageContainer wide>
        <SessionSkeleton />
      </PageContainer>
    );
  }

  if (error || !data) {
    return (
      <PageContainer wide>
        <ApiErrorView error={error} retry={() => void refetch()} context="this session" />
      </PageContainer>
    );
  }

  const deepLinkSec = Number(params.get('t') ?? '');
  const hasDeepLink = Number.isFinite(deepLinkSec) && deepLinkSec > 0;
  const resumeFromSec = hasDeepLink ? deepLinkSec : data.progress.lastPositionSec;

  const onCompleted = (): void => {
    toast({
      tone: 'success',
      title: 'Session complete',
      body: 'Marked as done. Nothing is graded — this just moves you along the track.',
    });
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: learnerKeys.course(courseSlug) });
    void queryClient.invalidateQueries({ queryKey: learnerKeys.courses() });
  };

  const present: Record<SessionTab, boolean> = {
    video: !!data.video,
    deck: data.decks.length > 0,
    notes: data.notes.length > 0,
    interactive: data.interactive.length > 0,
    quiz: !!data.quiz,
    assignment: !!data.assignment,
    // Folders with nothing in them are not worth a tab.
    resources: data.resources.resources.length > 0,
  };
  const shown = (tab: SessionTab): boolean => present[tab];
  const tabs = (Object.keys(TAB_META) as SessionTab[]).filter(shown);
  // Publishing needs at least one part, so only an author's preview of an
  // empty draft has no tab at all.
  const firstTab: SessionTab | null = tabs[0] ?? null;
  const activeTab: SessionTab | null =
    chosenTab && chosenTab.sessionId === data.id && shown(chosenTab.tab) ? chosenTab.tab : firstTab;
  // Adjusting state while rendering, React's pattern for "remember that this
  // happened": once opened, the deck stays mounted like every other panel.
  if (activeTab === 'deck' && deckOpenedFor !== data.id) setDeckOpenedFor(data.id);
  const deckOpened = deckOpenedFor === data.id || activeTab === 'deck';
  // Hidden, not unmounted, on the other tabs — the conversation lives in the
  // panel's state, so coming back to the notes must find it where it was left.
  const chatHere = data.chatEnabled && activeTab !== null && CHAT_TABS.has(activeTab);
  // Below the breakpoint the assistant is a drawer, opened and closed instead;
  // collapsing is the wide-screen equivalent.
  const chatShown = chatHere && (wide ? !chatCollapsed : chatOpen);

  const selectTab = (tab: SessionTab): void => {
    if (tab !== 'video') playerRef.current?.pause();
    if (!CHAT_TABS.has(tab)) setChatOpen(false);
    setChosenTab({ sessionId: data.id, tab });
  };

  const panel = (tab: SessionTab, children: ReactNode): ReactNode =>
    shown(tab) ? (
      <div
        role="tabpanel"
        id={`session-panel-${tab}`}
        aria-labelledby={`session-tab-${tab}`}
        hidden={activeTab !== tab}
      >
        {children}
      </div>
    ) : null;

  return (
    <PageContainer wide>
      <div className="mb-5">
        <Link
          to={`/courses/${data.courseSlug}`}
          className="text-sm text-ink-muted hover:text-ink inline-flex items-center gap-1.5"
        >
          <ArrowLeft size={14} aria-hidden />
          {data.courseTitle}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4 mt-2">
          <div className="min-w-0">
            {/* What the session contains is told by its tabs, so the header only
                carries where it sits, how long it takes and whether it is done. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1.5">
              <p className="eyebrow">Session {data.orderIndex + 1}</p>
              {data.estimatedDurationMin ? (
                <span className="eyebrow inline-flex items-center gap-1">
                  <Clock size={12} aria-hidden />
                  {formatMinutes(data.estimatedDurationMin)}
                </span>
              ) : null}
              {data.progress.completed ? <Badge tone="ok">Completed</Badge> : null}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-ink">{data.title}</h1>
            {data.summary ? (
              <p className="text-sm text-ink-muted mt-1.5 max-w-prose leading-relaxed">
                {data.summary}
              </p>
            ) : null}
          </div>
          {!data.video && !data.progress.completed && !data.isPreview ? (
            <Button
              size="sm"
              loading={markDone.isPending}
              icon={<CheckCircle2 size={14} aria-hidden />}
              onClick={() =>
                markDone.mutate(data.id, {
                  onSuccess: (res) => {
                    if (res.justCompleted) onCompleted();
                  },
                })
              }
            >
              Mark session as done
            </Button>
          ) : null}
        </div>
      </div>

      {data.isPreview ? (
        <Alert tone="warn" title="Unpublished draft — you are previewing your own work" className="mb-5">
          This session has not been published, so no learner can reach it. What you see here is
          what they will see once it goes live.
        </Alert>
      ) : null}

      <div
        className={clsx(
          'grid gap-6 items-start',
          // No assistant beside this tab means no column reserved for one.
          chatShown && wide && 'lg:grid-cols-[minmax(0,1fr)_23rem]',
        )}
      >
        <div className="min-w-0">
          {activeTab ? (
            <Tabs
              className="mb-5"
              idPrefix="session"
              label="Session contents"
              active={activeTab}
              onChange={selectTab}
              tabs={tabs.map((id) => ({
                id,
                label: (
                  <>
                    {TAB_META[id].icon}
                    {TAB_META[id].label}
                  </>
                ),
              }))}
            />
          ) : (
            <EmptyState
              icon={<FileText size={28} aria-hidden />}
              title="Nothing in this session yet"
              description="Add a video, deck, notes or any other part and it appears here as a tab."
            />
          )}

          {panel(
            'video',
            data.video ? (
              <VideoPlayer
                ref={playerRef}
                video={data.video}
                sessionId={data.id}
                title={data.title}
                resumeFromSec={resumeFromSec}
                resumeIsDeepLink={hasDeepLink}
                completed={data.progress.completed}
                onCompleted={onCompleted}
              />
            ) : null,
          )}

          {panel(
            'deck',
            deckOpened ? <DecksSection sessionId={data.id} decks={data.decks} /> : null,
          )}

          {panel('notes', <NotesSection blocks={data.notes} />)}

          {panel(
            'interactive',
            <InteractiveSection features={data.interactive} sessionQueryKey={queryKey} />,
          )}

          {panel(
            'quiz',
            data.quiz ? (
              <section aria-labelledby="session-quiz">
                <h2 id="session-quiz" className="sr-only">
                  Knowledge check
                </h2>
                <QuizRunner quiz={data.quiz} sessionId={data.id} />
              </section>
            ) : null,
          )}

          {panel(
            'assignment',
            data.assignment ? (
              <AssignmentSection assignment={data.assignment} sessionQueryKey={queryKey} />
            ) : null,
          )}

          {panel('resources', <ResourcesSection library={data.resources} />)}
        </div>

        {/*
          One ChatPanel. On a wide screen it is a sticky column beside the
          video; below the breakpoint the same element becomes a bottom drawer.
          `hidden` rather than unmounting, so closing the drawer does not throw
          the conversation away — only leaving the page does that.

          `chatEnabled` says whether the panel is available on this page at
          all. It does not say a model is attached — the panel reads that from
          /api/chat/config and labels its own replies as a stub.
        */}
        {data.chatEnabled ? (
          <div
            id="session-chat"
            hidden={!chatShown}
            className={clsx(
              wide
                ? 'sticky top-20 h-[calc(100vh-7rem)] min-h-96'
                : 'fixed inset-x-0 bottom-0 z-40 h-[80vh] px-3 pb-3 pt-0',
            )}
          >
            {!wide ? (
              <div className="flex justify-end pb-2">
                <button
                  type="button"
                  onClick={() => setChatOpen(false)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line-strong bg-surface px-3 py-1.5 text-xs font-medium text-ink shadow-pop"
                >
                  <X size={14} aria-hidden />
                  Close the assistant
                </button>
              </div>
            ) : null}
            <ChatPanel
              scopeType="session"
              scopeId={data.id}
              scopeLabel={`“${data.title}”`}
              onSeek={(sec) => {
                // A citation jumps into the lecture, so bring the lecture into view.
                if (present.video) selectTab('video');
                playerRef.current?.seekTo(sec);
              }}
              onOpenTab={(tab) => {
                // A citation names where a material lives; the tab is only
                // there when the session has it. Leaving the lecture tabs
                // hides the panel, but the conversation is kept until the
                // learner leaves the page.
                if (shown(tab)) selectTab(tab);
              }}
              headerAction={
                wide ? (
                  <IconButton
                    label="Collapse the assistant"
                    aria-expanded
                    aria-controls="session-chat"
                    onClick={() => setChatCollapsed(true)}
                  >
                    <PanelRightClose size={16} aria-hidden />
                  </IconButton>
                ) : undefined
              }
              className={clsx('h-full', !wide && 'shadow-pop')}
            />
          </div>
        ) : null}
      </div>

      {chatHere && !chatShown ? (
        <button
          type="button"
          aria-expanded={false}
          aria-controls="session-chat"
          onClick={() => (wide ? setChatCollapsed(false) : setChatOpen(true))}
          className="fixed bottom-4 right-4 z-30 inline-flex items-center gap-2 rounded-full border border-brand bg-brand px-4 py-3 text-sm font-semibold text-brand-ink shadow-pop"
        >
          <MessageSquare size={16} aria-hidden />
          Ask AI
        </button>
      ) : null}

      <Neighbours courseSlug={data.courseSlug} neighbours={data.neighbours} />
    </PageContainer>
  );
}

function Neighbours({
  courseSlug,
  neighbours,
}: {
  courseSlug: string;
  neighbours: { prev: { slug: string; title: string } | null; next: { slug: string; title: string } | null };
}) {
  if (!neighbours.prev && !neighbours.next) return null;

  return (
    <nav aria-label="Move between sessions" className="mt-10">
      <div className="grid gap-3 sm:grid-cols-2">
        {neighbours.prev ? (
          <Card className="hover:border-line-strong transition-colors">
            <Link
              to={`/courses/${courseSlug}/sessions/${neighbours.prev.slug}`}
              className="flex items-center gap-3 p-4 rounded-card"
            >
              <ArrowLeft size={16} className="text-ink-subtle shrink-0" aria-hidden />
              <span className="min-w-0">
                <span className="block text-xs text-ink-subtle">Previous session</span>
                <span className="block text-sm font-medium text-ink truncate">
                  {neighbours.prev.title}
                </span>
              </span>
            </Link>
          </Card>
        ) : (
          <span />
        )}
        {neighbours.next ? (
          <Card className="hover:border-line-strong transition-colors sm:col-start-2">
            <Link
              to={`/courses/${courseSlug}/sessions/${neighbours.next.slug}`}
              className="flex items-center justify-end gap-3 p-4 rounded-card text-right"
            >
              <span className="min-w-0">
                <span className="block text-xs text-ink-subtle">Next session</span>
                <span className="block text-sm font-medium text-ink truncate">
                  {neighbours.next.title}
                </span>
              </span>
              <ArrowRight size={16} className="text-ink-subtle shrink-0" aria-hidden />
            </Link>
          </Card>
        ) : null}
      </div>
    </nav>
  );
}
